// src/api/axiosInstance.ts
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

// ✅ _retry 필드를 확장하기 위한 커스텀 타입
interface CustomRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * BASE URL 설정
 * - .env에 REACT_APP_API_BASE_URL이 없으면 localhost:8000 사용
 * - 끝에 붙은 슬래시는 제거 (http://localhost:8000/ -> http://localhost:8000)
 */
const rawBaseURL =
  process.env.REACT_APP_API_BASE_URL ?? "http://localhost:8000";
const baseURL = rawBaseURL.replace(/\/+$/, "");

/**
 * 스토리지 키 (AuthContext와 반드시 맞춰 쓸 것!)
 * - 프론트 로그인/회원가입 성공 시 여기에 저장되도록 구현해야 함
 */
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const REMEMBER_ME_KEY = "remember_me";

export const getAccessToken = (): string | null => {
  // 두 스토리지 모두 확인 (로그인 유지 설정 변경 대응)
  return localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (access: string, refresh: string, rememberMe: boolean = true) => {
  // 먼저 기존 토큰 모두 정리
  clearTokens();

  // rememberMe 설정 저장
  localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString());

  // 선택된 스토리지에 토큰 저장
  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem(ACCESS_TOKEN_KEY, access);
  storage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const clearTokens = () => {
  // 두 스토리지 모두에서 토큰 삭제
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const getRememberMe = (): boolean => {
  return localStorage.getItem(REMEMBER_ME_KEY) === "true";
};

/**
 * ✅ 메인 API 인스턴스: 기본 prefix를 /api로 설정
 *   예: baseURL=http://localhost:8000 -> http://localhost:8000/api
 */
const api = axios.create({
  baseURL: `${baseURL}/api`,
});

/**
 * ✅ refresh 전용 클라이언트(인터셉터 없음) → 무한루프 방지
 */
const authClient = axios.create({
  baseURL: `${baseURL}/api`,
});

/**
 * ✅ 요청 인터셉터: access token 자동 첨부
 */
api.interceptors.request.use(
  (config: CustomRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers ?? {};
      // 이미 Authorization을 직접 넣지 않았으면 자동 세팅
      if (!config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * ✅ 응답 인터셉터: access token 자동 갱신
 */
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomRequestConfig | undefined;
    const status = error.response?.status;

    // 방어: config가 없으면 그대로 throw
    if (!originalRequest) return Promise.reject(error);

    // refresh 요청 자체는 재시도하지 않도록 보호
    const isRefreshCall =
      originalRequest.url?.includes("/token/refresh/") ?? false;

    // 로그인/회원가입 요청은 401 인터셉터에서 제외 (에러 메시지가 페이지에서 직접 처리됨)
    const isAuthCall =
      originalRequest.url?.includes("/auth/login/") ||
      originalRequest.url?.includes("/auth/signup/") ||
      originalRequest.url?.includes("/accounts/login/") ||
      originalRequest.url?.includes("/accounts/signup/");

    // 401이면 토큰 만료/무효 가능성이 높으므로 refresh 시도
    if (status === 401 && !isRefreshCall && !isAuthCall && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.warn("⛔ refresh_token 없음 → 로그인 필요");
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        // ✅ refresh는 authClient로 (인터셉터 루프 방지)
        const res = await authClient.post(
          "/token/refresh/",
          { refresh: refreshToken }
          // Content-Type은 axios가 자동으로 application/json으로 설정하므로 따로 헤더 지정 X
        );

        const data = res.data as any;
        const newAccessToken = data.access as string | undefined;
        const newRefreshToken =
          (data.refresh as string | undefined) ?? refreshToken;

        if (!newAccessToken) {
          throw new Error("refresh 응답에 access 토큰이 없습니다.");
        }

        // 토큰 갱신 (원래 rememberMe 설정 유지)
        setTokens(newAccessToken, newRefreshToken, getRememberMe());

        // 기본 헤더 갱신
        api.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;

        // 원 요청에도 헤더 갱신 후 재시도
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        console.error("🔒 토큰 갱신 실패 → 로그인 필요", refreshError);
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // 그 외 에러는 그대로 넘김
    return Promise.reject(error);
  }
);

export default api;
